import qs from 'query-string';

import appSettings from 'wanderu-config';
import {
    i18nMoney,
    selectLocale,
    selectCurrency,
} from 'wanderu-i18n';
import { getLogger } from 'wanderu-logger';
import { selectDetailedPricingStatus, selectOrderId } from 'wanderu-orders';
import { SnowplowService } from 'wanderu-service-analytics';
import { OrdersService } from 'wanderu-service-wapi';

import {
    actions,
    namespace,
    reducer,
    selectors,
} from './base';
import {
    actions as bookingActions,
    selectors as bookingSelectors,
} from '../../appstate';
import { FORM_VERSION } from '../utils';

const logger = getLogger('wanderu-app-booking:makePaymentsCall');

function makePaymentsCall(state, component, adyenActions) {
    return async (dispatch, getState) => {
        await dispatch(actions.reset());

        dispatch(actions.startWork());

        let error;
        let meta;
        try {
            const locale = selectLocale(getState());
            const location = locale.split('-')[1];
            const countryCode = location.toUpperCase();

            const priceList = bookingSelectors.selectPaymentPriceList(getState());
            const total = i18nMoney(priceList)?.getTotalPrice();
            const currency = selectCurrency(getState());
            const detailedPricingStatus = selectDetailedPricingStatus(getState());

            const {
                bookingSessionId,
                form,
                itineraryId,
                seats,
                selectedFaresMap,
                tokenMap = {},
                tripIds,
                wheelchairs,
            } = bookingSelectors.selectBookingInfo(getState());

            // PLAT-198 PR 5: Remove alongside the selectPriceList legacy
            // fallback — this warning monitors a code path that goes away.
            if (detailedPricingStatus !== 'succeeded') {
                logger.warn('legacyPricingFallback', {
                    bookingSessionId,
                    detailedPricingStatus,
                    itineraryId,
                });
            }

            const body = {
                accessibility: {
                    wheelchairs,
                },
                booking_session_id: bookingSessionId,
                cacheTokens: Object.values(tokenMap),
                countryCode,
                currency,
                form_submission: form,
                itinerary_id: itineraryId,
                locale,
                meta: {
                    form_version: FORM_VERSION,
                    mobile: appSettings.env.isClient
                        ? window.matchMedia('(max-width: 980px)').matches
                        : undefined,
                },
                passengers: seats,
                payment: {
                    ...state.data,
                    amount: {
                        currency,
                        value: total.toFixed(2),
                    },
                },
                selected_fares: selectedFaresMap,
                trip_ids: tripIds,
            };

            const orderId = selectOrderId(getState());

            if (!orderId) {
                logger.warn('missingOrderId', { bookingSessionId, itineraryId });
            }

            const query = qs.stringify({ orderId });
            const url = query
                ? `wtix/v4/book/payment/auth?${query}`
                : 'wtix/v4/book/payment/auth';

            const { result, error: adyenError } = await OrdersService.post(url, { body });

            if (!result?.payment?.resultCode) {
                adyenActions.reject(adyenError);
                return;
            }

            const {
                action,
                donationToken,
                order,
                reference,
                resultCode,
            } = result.payment;

            dispatch(bookingActions.setPayment({ ...state?.data, reference }));

            // Resolve with payment result — must be called even if the payment is unsuccessful.
            adyenActions.resolve({
                action,
                donationToken,
                order,
                resultCode,
            });
        } catch (ex) {
            error = ex?.responseBody?.error || ex;

            adyenActions.reject(error);

            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.error('fetch', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });
        } finally {
            dispatch(actions.endWork(undefined, meta, error));
        }
    };
}

const makePaymentsCallAction = {
    fetch: makePaymentsCall,
    ...actions,
    ...selectors,
};

export {
    makePaymentsCallAction,
};

export default {
    [namespace]: reducer,
};
